import React, { useState } from 'react';
import { ImageFeedbackEditor } from '@components/ImageFeedbackEditor';

// Sample public images for testing (no CORS issues)
const TEST_IMAGES = [
  {
    label: 'Landscape',
    url: 'https://picsum.photos/seed/mountain/800/500',
  },
  {
    label: 'Portrait',
    url: 'https://picsum.photos/seed/portrait/500/700',
  },
  {
    label: 'Square',
    url: 'https://picsum.photos/seed/square/600/600',
  },
];

const ImageEditorTestPage: React.FC = () =>
{
  const [editorOpen, setEditorOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(TEST_IMAGES[0]?.url ?? '');
  const [customUrl, setCustomUrl] = useState('');
  const [savedImage, setSavedImage] = useState<string | null>(null);
  const [accentColor, setAccentColor] = useState('#3B82F6');

  const handleOpen = (url: string) =>
  {
    setSelectedImage(url);
    setSavedImage(null);
    setEditorOpen(true);
  };

  const handleSave = (base64: string) =>
  {
    setSavedImage(base64);
    setEditorOpen(false);
  };

  return (
    <div style={{ padding: 32, fontFamily: 'sans-serif', maxWidth: 900 }}>
      <h2 style={{ marginBottom: 8 }}>ImageFeedbackEditor — Test Page</h2>
      <p style={{ color: '#666', marginBottom: 24 }}>
        Select an image below to open the editor. After saving, the annotated image appears at the bottom.
      </p>

      {/* Accent color picker */}
      <div style={{ marginBottom: 20, display: 'flex', alignItems: 'center', gap: 10 }}>
        <label style={{ fontWeight: 500 }}>Accent color:</label>
        <input
          type="color"
          value={accentColor}
          onChange={e => setAccentColor(e.target.value)}
          style={{ width: 36, height: 36, border: 'none', cursor: 'pointer' }}
        />
        <span style={{ color: '#888', fontSize: 13 }}>{accentColor}</span>
      </div>

      {/* Preset images */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
        {TEST_IMAGES.map(img => (
          <button
            key={img.label}
            onClick={() => handleOpen(img.url)}
            style={{
              padding: '8px 18px',
              borderRadius: 6,
              border: '1px solid #ddd',
              background: '#f5f5f5',
              cursor: 'pointer',
              fontWeight: 500,
            }}
          >
            Open: {img.label}
          </button>
        ))}
      </div>

      {/* Custom URL */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 32, alignItems: 'center' }}>
        <input
          type="text"
          placeholder="Paste any image URL…"
          value={customUrl}
          onChange={e => setCustomUrl(e.target.value)}
          style={{
            flex: 1,
            padding: '7px 12px',
            borderRadius: 5,
            border: '1px solid #ccc',
            fontSize: 14,
          }}
        />
        <button
          onClick={() => customUrl && handleOpen(customUrl)}
          disabled={!customUrl}
          style={{
            padding: '7px 16px',
            borderRadius: 5,
            border: 'none',
            background: accentColor,
            color: '#fff',
            cursor: customUrl ? 'pointer' : 'not-allowed',
            opacity: customUrl ? 1 : 0.5,
            fontWeight: 500,
          }}
        >
          Open
        </button>
      </div>

      {/* Saved result */}
      {savedImage && (
        <div>
          <h3 style={{ marginBottom: 8 }}>Saved result:</h3>
          <img
            src={savedImage}
            alt="Annotated"
            style={{
              maxWidth: '100%',
              border: '2px solid #e0e0e0',
              borderRadius: 6,
              boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
            }}
          />
          <div style={{ marginTop: 10 }}>
            <a
              href={savedImage}
              download="annotated.png"
              style={{ color: accentColor, textDecoration: 'none', fontWeight: 500 }}
            >
              Download annotated.png
            </a>
          </div>
        </div>
      )}

      {/* The editor */}
      {editorOpen && (
        <ImageFeedbackEditor
          imageSrc={selectedImage}
          onSave={handleSave}
          onCancel={() => setEditorOpen(false)}
          accentColor={accentColor}
        />
      )}
    </div>
  );
};

export default ImageEditorTestPage;
